import { type PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import {
  DEFAULT_UNSAVED_CHANGES_MESSAGE,
  locationToUrl,
  UnsavedChangesContext,
  type UnsavedChangesContextValue,
} from '@/shared/lib/unsavedChangesContext'
import { shouldPromptUnsavedChangesOnPopState } from '@/shared/lib/unsavedChangesGuard'
import { registerUnsavedChangesBackCoordinator } from '@/shared/lib/unsavedChangesBackCoordinator'
import { useConfirmDialog } from '@/shared/ui/useConfirmDialog'

export const UnsavedChangesProvider = ({ children }: PropsWithChildren) => {
  const location = useLocation()
  const { confirm } = useConfirmDialog()
  const [isDirty, setDirtyState] = useState(false)

  const stableUrlRef = useRef<string>('')
  const locationRef = useRef(location)
  const dirtyRef = useRef(false)
  const confirmMessageRef = useRef(DEFAULT_UNSAVED_CHANGES_MESSAGE)
  const isConfirmingRef = useRef(false)

  const setDirty = useCallback((next: boolean) => {
    dirtyRef.current = next
    setDirtyState(next)
  }, [])

  const confirmDiscard = useCallback(async () => {
    return await confirm({
      title: 'Sair sem salvar?',
      description: confirmMessageRef.current,
      confirmLabel: 'Sair',
      cancelLabel: 'Continuar',
    })
  }, [confirm])

  useEffect(() => {
    return registerUnsavedChangesBackCoordinator({
      isDirty: () => dirtyRef.current,
      confirmDiscard,
      clearDirty: () => setDirty(false),
    })
  }, [confirmDiscard, setDirty])

  useEffect(() => {
    locationRef.current = location
  }, [location])

  useEffect(() => {
    dirtyRef.current = isDirty
  }, [isDirty])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    if (!dirtyRef.current) {
      stableUrlRef.current = locationToUrl(location)
    }
  }, [isDirty, location])

  useEffect(() => {
    const handlePopState = () => {
      const targetUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`
      const safeStableUrl = stableUrlRef.current || locationToUrl(locationRef.current)
      const shouldPrompt = shouldPromptUnsavedChangesOnPopState({
        isDirty: dirtyRef.current,
        isConfirming: isConfirmingRef.current,
        historyState: window.history.state,
        targetUrl,
        stableUrl: safeStableUrl,
      })
      if (!shouldPrompt) return

      window.history.pushState(null, '', safeStableUrl)

      isConfirmingRef.current = true
      void (async () => {
        try {
          const shouldLeave = await confirmDiscard()
          if (!shouldLeave) {
            return
          }

          setDirty(false)
          window.history.go(-2)
        } finally {
          isConfirmingRef.current = false
        }
      })()
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [confirmDiscard, setDirty])

  const value = useMemo<UnsavedChangesContextValue>(
    () => ({ isDirty, setDirty, confirmDiscard }),
    [confirmDiscard, isDirty, setDirty],
  )

  return <UnsavedChangesContext.Provider value={value}>{children}</UnsavedChangesContext.Provider>
}

